import { unpublishedChangesKeys } from '../agents/agent-template-api';
import { ToolHistory } from './ToolHistory';
import { toolHistoryKeys } from './tool-history-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { buildAiToolEditPath } from '../../app-routes';
import { PageHeader } from '../../components/common/PageHeader';
import {
  partnerApi,
  type ApiError,
  type ToolDefinition,
  type ToolDefinitionInput,
  type ToolExecutionKind,
} from '../../lib/api';
import { LookupToolForm } from './LookupToolForm';
import {
  buildToolUpdatePatch,
  createHttpDraft,
  createLookupDraft,
  isStatusOnlyDeactivationPatch,
  toolDefinitionToInput,
} from './lookup-tool-model';
import { ToolForm } from './ToolForm';
import { ToolTestPanel } from './ToolTestPanel';
import { toolReturnState, useToolReturnPath } from './tool-return-path';
import { ToolTypeChooser } from './ToolTypeChooser';
import { ToolSaveBar, type ToolSaveState } from './ToolSaveBar';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';
import styles from './LookupTool.module.css';

const TYPE_LABELS = {
  http: 'HTTP API',
  schema_lookup: 'Synced schema lookup',
} as const satisfies Record<ToolExecutionKind, string>;

const CHANGE_TYPE_MESSAGE =
  'Changing the execution type will discard the configuration you entered for this tool. Continue?';
const TOOL_CONFIGURATION_LEGEND = 'Tool configuration';

interface UpdateSubmission {
  toolId: string;
  patch: Partial<ToolDefinitionInput>;
}

const SAVE_ERROR_FALLBACK = 'Unable to save tool';

const formatSaveError = (error: ApiError): string => {
  const responseData = error.response?.data;
  const details =
    responseData?.details?.map(detail => detail.trim()).filter(detail => detail.length > 0) ?? [];
  const errors =
    responseData?.errors
      ?.map(detail => {
        if (typeof detail === 'string') return detail.trim();
        const path = detail.path.join('.');
        return path ? `${path}: ${detail.message}` : detail.message;
      })
      .filter(detail => detail.length > 0) ?? [];
  const validationMessages = details.length > 0 ? details : errors;
  const message = error.message.trim() || SAVE_ERROR_FALLBACK;

  return [message, ...validationMessages].join('\n');
};

export const ToolEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const returnPath = useToolReturnPath();
  const queryClient = useQueryClient();
  const isNew = !id || id === 'new';

  const [draft, setDraft] = useState<ToolDefinitionInput | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<ToolDefinition | null>(null);
  const [seededId, setSeededId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasLocalInteraction, setHasLocalInteraction] = useState(false);
  const [formRevision, setFormRevision] = useState(0);
  const [isChoosingType, setIsChoosingType] = useState(isNew);
  const [staleLookupConfiguration, setStaleLookupConfiguration] = useState(false);
  const [latestSourceResponse, setLatestSourceResponse] = useState<{
    toolId: string;
    body: unknown;
  } | null>(null);

  const existing = useQuery({
    queryKey: ['ai-tools', id],
    queryFn: () => partnerApi.getTool(id as string),
    enabled: !isNew && Boolean(id),
  });

  // Seed once per saved tool so background refetches cannot clobber in-progress edits.
  if (!isNew && existing.data?.id === id && seededId !== id) {
    setSeededId(id ?? null);
    setSavedSnapshot(existing.data);
    setDraft(toolDefinitionToInput(existing.data));
    setIsChoosingType(false);
    setIsFormValid(true);
    setHasLocalInteraction(false);
    setSaveError(null);
    setStaleLookupConfiguration(false);
    setFormRevision(revision => revision + 1);
  }

  const createMutation = useMutation({
    mutationFn: (input: ToolDefinitionInput) => partnerApi.createTool(input),
    onSuccess: created => {
      queryClient.setQueryData(['ai-tools', created.id], created);
      queryClient.invalidateQueries({ queryKey: ['ai-tools'] });
      setSavedSnapshot(created);
      setSeededId(created.id);
      setDraft(toolDefinitionToInput(created));
      setFormRevision(revision => revision + 1);
      const editPath = buildAiToolEditPath(created.id);
      allowNextNavigation(editPath);
      navigate(editPath, { state: toolReturnState(returnPath) });
      setSaveError(null);
      setStaleLookupConfiguration(false);
      setHasLocalInteraction(false);
    },
    onError: (error: ApiError) => setSaveError(formatSaveError(error)),
  });

  const updateMutation = useMutation({
    mutationFn: (submission: UpdateSubmission) =>
      partnerApi.updateTool(submission.toolId, submission.patch),
    onSuccess: (updated, submission) => {
      queryClient.setQueryData(['ai-tools', submission.toolId], updated);
      queryClient.invalidateQueries({
        predicate: query => query.queryKey[0] === 'ai-tools' && query.queryKey[1] !== submission.toolId,
      });
      queryClient.invalidateQueries({ queryKey: toolHistoryKeys.detail(submission.toolId) });
      queryClient.invalidateQueries({ queryKey: unpublishedChangesKeys.all });
      if (id !== submission.toolId) return;

      setSavedSnapshot(updated);
      setSaveError(null);
      setDraft(toolDefinitionToInput(updated));
      setStaleLookupConfiguration(false);
      setHasLocalInteraction(false);
    },
    onError: (error: ApiError, submission) => {
      if (id !== submission.toolId) return;
      setSaveError(formatSaveError(error));
    },
  });

  const savedTool = !isNew && savedSnapshot?.id === id ? savedSnapshot : null;
  const draftBelongsToCurrentRoute = isNew ? seededId === null : savedTool !== null;
  const activeDraft = draftBelongsToCurrentRoute ? draft : null;
  const patchWithoutForcedLookupConfiguration =
    savedTool && activeDraft ? buildToolUpdatePatch({ saved: savedTool, draft: activeDraft }) : {};
  const shouldForceLookupConfiguration =
    staleLookupConfiguration &&
    savedTool?.execution_kind === 'schema_lookup' &&
    activeDraft?.execution_kind === 'schema_lookup' &&
    !isStatusOnlyDeactivationPatch(patchWithoutForcedLookupConfiguration);
  const patch = shouldForceLookupConfiguration
    ? {
        ...patchWithoutForcedLookupConfiguration,
        lookup_config: activeDraft.lookup_config,
      }
    : patchWithoutForcedLookupConfiguration;
  const canSaveStatusOnlyDeactivation = isStatusOnlyDeactivationPatch(patch);
  const hasPatch = Object.keys(patch).length > 0;
  const hasDraftPatch = Object.keys(patchWithoutForcedLookupConfiguration).length > 0;
  const invalidLocalEditorHasChanges = hasLocalInteraction && !isFormValid;
  const existingToolHasUnsavedChanges =
    activeDraft !== null && (hasDraftPatch || invalidLocalEditorHasChanges);
  const hasUnsavedChanges = isNew ? activeDraft !== null : existingToolHasUnsavedChanges;
  const allowNextNavigation = useUnsavedChangesGuard(hasUnsavedChanges);

  const handleSave = () => {
    if (!activeDraft) return;
    setSaveError(null);
    if (isNew) {
      createMutation.mutate(activeDraft);
      return;
    }
    if (!savedTool || !hasPatch) {
      return;
    }
    updateMutation.mutate({
      toolId: savedTool.id,
      patch,
    });
  };

  const handleTypeSelect = (kind: ToolExecutionKind) => {
    if (activeDraft?.execution_kind === kind) {
      setIsChoosingType(false);
      return;
    }
    if (activeDraft && !window.confirm(CHANGE_TYPE_MESSAGE)) return;

    setDraft(
      kind === 'http'
        ? createHttpDraft(activeDraft ?? undefined)
        : createLookupDraft(activeDraft ?? undefined),
    );
    setSeededId(null);
    setIsChoosingType(false);
    setIsFormValid(false);
    setStaleLookupConfiguration(false);
    setHasLocalInteraction(true);
    setSaveError(null);
  };

  const handleDraftChange = (next: ToolDefinitionInput) => {
    setDraft(next);
    setHasLocalInteraction(true);
    setSaveError(null);
  };

  const handleFormInteraction = () => {
    setHasLocalInteraction(true);
    setSaveError(null);
  };

  const handleDiscard = () => {
    createMutation.reset();
    updateMutation.reset();
    setSaveError(null);
    setHasLocalInteraction(false);
    setStaleLookupConfiguration(false);
    setFormRevision(revision => revision + 1);
    if (isNew) {
      setDraft(null);
      setIsChoosingType(true);
      setIsFormValid(false);
      return;
    }
    if (savedTool) {
      setDraft(toolDefinitionToInput(savedTool));
      setIsFormValid(true);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const formIsValidOrStatusOnlyDeactivation = isFormValid || canSaveStatusOnlyDeactivation;
  const existingToolIsUnchanged = !isNew && !hasPatch;
  const saveDisabled =
    !activeDraft || isSaving || !formIsValidOrStatusOnlyDeactivation || existingToolIsUnchanged;
  const headerTitle = isNew ? 'New tool' : savedTool?.name || 'Edit tool';
  const headerDescription = isNew
    ? 'Choose how this tool runs, then configure what the agent can do.'
    : savedTool?.description || '';
  const sampleResponseForCurrentTool =
    latestSourceResponse && latestSourceResponse.toolId === savedTool?.id
      ? latestSourceResponse.body
      : undefined;
  let saveState: ToolSaveState = 'saved';
  if (hasUnsavedChanges) saveState = 'dirty';
  if (saveError) saveState = 'error';
  if (isSaving) saveState = 'saving';

  return (
    <main className={styles.page}>
      <PageHeader
        title={headerTitle}
        description={headerDescription}
        right={
          <nav className={styles.headerActions} aria-label="Tool actions">
            <button type="button" onClick={() => navigate(returnPath)} disabled={isNew && isSaving}>
              Back
            </button>
          </nav>
        }
      />

      {activeDraft && (
        <ToolSaveBar
          state={saveState}
          isNew={isNew}
          saveDisabled={saveDisabled}
          discardDisabled={!hasUnsavedChanges || isSaving}
          error={saveError}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      )}

      {isChoosingType || (isNew && !activeDraft) ? (
        <ToolTypeChooser onSelect={handleTypeSelect} />
      ) : (
        activeDraft && (
          <>
            <section className={styles.typeBar}>
              <div>
                <span>Execution type</span>
                <strong>{TYPE_LABELS[activeDraft.execution_kind]}</strong>
              </div>
              {isNew && (
                <button type="button" onClick={() => setIsChoosingType(true)} disabled={isSaving}>
                  Change type
                </button>
              )}
            </section>

            <section className={styles.editorLayout}>
              <article className={styles.formCard}>
                <fieldset className={styles.formFields} disabled={isSaving} aria-busy={isSaving}>
                  <legend className={styles.formFieldsLegend}>{TOOL_CONFIGURATION_LEGEND}</legend>
                  {activeDraft.execution_kind === 'http' ? (
                    <ToolForm
                      key={`${activeDraft.execution_kind}:${formRevision}`}
                      draft={activeDraft}
                      onChange={handleDraftChange}
                      isEditing={!isNew}
                      onValidityChange={setIsFormValid}
                      onInteraction={handleFormInteraction}
                      canonicalName={savedTool?.name}
                      sampleResponse={sampleResponseForCurrentTool}
                    />
                  ) : (
                    <LookupToolForm
                      key={`${activeDraft.execution_kind}:${formRevision}`}
                      draft={activeDraft}
                      onChange={handleDraftChange}
                      isEditing={!isNew}
                      canonicalName={savedTool?.name}
                      storedConfig={
                        savedTool?.execution_kind === 'schema_lookup'
                          ? savedTool.lookup_config
                          : undefined
                      }
                      onValidityChange={setIsFormValid}
                      onStaleConfigurationChange={setStaleLookupConfiguration}
                    />
                  )}
                </fieldset>
              </article>

              {!isNew && savedTool && (
                <aside className={styles.testPanel}>
                  <ToolTestPanel
                    key={savedTool.id}
                    tool={savedTool}
                    onSourceResponse={body => setLatestSourceResponse({ toolId: savedTool.id, body })}
                  />
                </aside>
              )}
            </section>
          </>
        )
      )}
      {savedTool && <ToolHistory key={savedTool.id} toolId={savedTool.id} />}
    </main>
  );
};
